/**
 * Single source of truth for every status label, description and colour.
 *
 * COLOUR MEANS "WHOSE COURT IS THE BALL IN". That is the whole rule:
 *
 *   gray     nobody - not in play yet
 *   blue     a reviewer is holding it
 *   violet   audited, waiting on a director
 *   indigo   decided at this stage, still in flight
 *   amber    the CLAIMANT has to act
 *   orange   the ADMIN has to act
 *   emerald  terminal, good (solid fill)
 *   red      terminal, bad (solid fill)
 *
 * The previous scheme coloured by "stage of pipeline", which put
 * `submitted`, `under_review`, `auditor_approved` and
 * `regional_director_approved` all in the same blue - so a claim an auditor had
 * sent back looked exactly like one nobody had touched.
 *
 * Labels name BOTH the sender and the actor wherever a status could otherwise be
 * confused with a neighbour. "Returned" alone was used for two different states
 * and read identically in four places; the three send-back states are now
 * "Returned to Claimant", "Returned by Auditor - Admin to Fix" and
 * "Sent Back for Revision", which cannot be mistaken for one another.
 *
 * Terminal states are the only SOLID fills, so "this is finished" is legible at
 * a glance without reading the word.
 */

import { namedCoverActive, coverEndText } from './namedCover.mjs';

/**
 * bucket -> badge classes, border, and a stronger fill for card dots.
 *
 * decorate() also splits `badge` into `bg`/`text`, so a caller that needs only
 * the surface colour does not have to pick the class string apart with a regex.
 */
const BUCKET = {
  gray:    { badge: 'bg-gray-100 text-gray-700',        border: 'border-gray-300',    solid: 'bg-gray-500' },
  blue:    { badge: 'bg-blue-100 text-blue-800',        border: 'border-blue-300',    solid: 'bg-blue-600' },
  violet:  { badge: 'bg-violet-100 text-violet-900',    border: 'border-violet-300',  solid: 'bg-violet-600' },
  indigo:  { badge: 'bg-indigo-100 text-indigo-800',    border: 'border-indigo-300',  solid: 'bg-indigo-600' },
  amber:   { badge: 'bg-amber-100 text-amber-900',      border: 'border-amber-400',   solid: 'bg-amber-500' },
  orange:  { badge: 'bg-orange-100 text-orange-900',    border: 'border-orange-500',  solid: 'bg-orange-600' },
  emerald: { badge: 'bg-emerald-600 text-white',        border: 'border-emerald-700', solid: 'bg-emerald-600' },
  red:     { badge: 'bg-red-600 text-white',            border: 'border-red-700',     solid: 'bg-red-600' },
  // Non-terminal rejection on a single report line, so not a solid fill.
  redSoft: { badge: 'bg-red-100 text-red-800',          border: 'border-red-300',     solid: 'bg-red-500' },
  slate:   { badge: 'bg-slate-100 text-slate-700',      border: 'border-slate-300',   solid: 'bg-slate-500' },
};

/* ============================================================ CLAIM STATUSES */
/**
 * `description` is written for whoever is reading the screen and answers "what
 * does this mean for me now". `selfLabel`/`selfDescription` are used when the
 * reader is the claimant, because "Admin to Fix" is the wrong instruction to
 * give the person who must not act.
 */
const CLAIM = {
  draft: {
    bucket: 'gray',
    label: 'Draft',
    description: 'Not submitted. Only the claimant can see it.',
  },
  submitted: {
    bucket: 'blue',
    label: 'Awaiting Admin Review',
    description: 'With the regional admin for first review.',
    selfDescription: 'With your regional admin for first review.',
  },
  // Legacy rows only. Labelled so it is filterable and obviously the same stage,
  // without silently merging into 'submitted' (which used to hide it entirely).
  pending: {
    bucket: 'blue',
    label: 'Awaiting Admin Review (legacy)',
    description: 'An older record at the same stage as Awaiting Admin Review.',
  },
  approved: {
    bucket: 'indigo',
    label: 'Admin Approved - Awaiting Audit',
    description: 'The admin signed off. The auditor has not checked it yet.',
    selfLabel: 'Approved - Awaiting Audit',
    selfDescription: 'Approved by your admin and waiting on the auditor. Nothing for you to do.',
  },
  under_review: {
    bucket: 'orange',
    label: 'Returned by Auditor - Admin to Fix',
    description:
      'The auditor queried it, or the admin withdrew their approval. Back with the admin; the claimant does nothing.',
    selfLabel: 'With Admin (Auditor Query)',
    selfDescription: 'The auditor raised a query and your admin is correcting it. No action needed from you.',
  },
  returned: {
    bucket: 'amber',
    label: 'Returned to Claimant',
    description: 'The admin sent it back to the claimant to edit and resubmit.',
    selfLabel: 'Returned to You - Action Needed',
    selfDescription: 'Your admin sent this back. Edit it and resubmit.',
  },
  rejected: {
    bucket: 'red',
    label: 'Rejected - Closed',
    description: 'Declined. This claim is closed and will not be paid.',
    terminal: true,
  },
  paid: {
    bucket: 'emerald',
    label: 'Approved for Payment',
    description: 'On a fully signed monthly report and released for payment.',
    terminal: true,
  },
  /**
   * The end of an INDEPENDENT CONTRACTOR's claim.
   *
   * A staff claim reaches 'paid' through a monthly report signed by the
   * directors. A contractor has no report stage - the auditor is their last
   * approver - so their claim would otherwise sit at 'approved' forever,
   * indistinguishable from one still waiting on someone.
   */
  finally_approved: {
    bucket: 'emerald',
    label: 'Finally Approved',
    description: 'Approved by the auditor, who is the final approver for contractor claims.',
    selfLabel: 'Fully Approved',
    selfDescription: 'Fully approved. Nothing further is needed from you.',
    terminal: true,
  },
};

/** Workflow order for claim filters and card strips. */
export const CLAIM_ORDER = [
  'draft', 'submitted', 'pending', 'approved', 'under_review', 'returned', 'rejected',
  'finally_approved', 'paid',
];

/**
 * Which claim statuses get a summary card. 'pending' is excluded: it is a legacy
 * duplicate of 'submitted' and a card for it would read zero on every modern
 * data set while implying something was missing.
 */
export const CLAIM_CARD_SET = [
  'draft', 'submitted', 'approved', 'under_review', 'returned', 'rejected',
  'finally_approved', 'paid',
];

/**
 * The same set minus 'draft', for admin and reviewer screens.
 *
 * A draft belongs to its author until they submit it; nobody else sees one, and
 * the admin API does not return them. A card for drafts on an admin screen
 * would read zero forever while implying the admin was missing something.
 * The claimant's own dashboard uses CLAIM_CARD_SET, where draft is theirs.
 */
export const ADMIN_CLAIM_CARD_SET = CLAIM_CARD_SET.filter((s) => s !== 'draft');

/* =========================================================== REPORT STATUSES */
const REPORT = {
  draft: {
    bucket: 'gray',
    label: 'Draft Report',
    description: 'Being assembled by the admin. Nobody has been notified.',
  },
  submitted: {
    bucket: 'blue',
    label: 'Pending Auditor Review',
    description: 'Sent to the auditor. No one has signed yet.',
  },
  auditor_approved: {
    bucket: 'violet',
    label: 'Audited - Pending Regional Director',
    description: 'The auditor signed. Waiting on the regional director.',
  },
  regional_director_approved: {
    bucket: 'indigo',
    label: 'Regional Approved - Pending Final Approval',
    description: 'Waiting on the global or finance director for final approval.',
  },
  global_director_approved: {
    bucket: 'emerald',
    label: 'Fully Approved',
    description: 'Signed at every stage. Its claims are released for payment.',
    terminal: true,
  },
  // Orange, not red: a sent-back report is WORK FOR THE ADMIN, not a terminal
  // refusal. Red here is what made a rejected report look identical to a
  // rejected claim, which is closed for good.
  rejected: {
    bucket: 'orange',
    label: 'Sent Back for Revision',
    description:
      'A reviewer returned it to the admin. This report is closed; the admin fixes the claims and generates a new one.',
  },
};

export const REPORT_ORDER = [
  'draft', 'submitted', 'auditor_approved', 'regional_director_approved',
  'global_director_approved', 'rejected',
];

/** Kept for existing importers; same list, named as before. */
export const REPORT_STATUSES = REPORT_ORDER;

export const REPORT_CARD_SET = REPORT_ORDER;

/* ================================================= REPORT LINE (per claim row) */
const REPORT_CLAIM = {
  pending: {
    bucket: 'slate',
    label: 'Not Yet Reviewed',
    description: 'On the report. No reviewer has signed this line.',
  },
  approved: {
    bucket: 'indigo',
    label: 'Line Approved',
    description: 'This line was signed off on the report.',
  },
  returned: {
    bucket: 'orange',
    label: 'Line Sent Back to Admin',
    description: 'A reviewer flagged this line. The underlying claim is now Returned by Auditor.',
  },
  rejected: {
    bucket: 'redSoft',
    label: 'Line Rejected',
    description: 'This line was rejected on the report.',
  },
};

export const REPORT_CLAIM_ORDER = ['pending', 'approved', 'returned', 'rejected'];

/* ==================================================== APPROVAL CHAIN STAGES */
/**
 * One stage vocabulary, shared by the approval trail, the claim history panel
 * and the claim PDF. Three separate maps had drifted apart, so the same event
 * was described differently depending on where you looked at it.
 */
const STAGE = {
  draft: 'Draft created',
  submitted: 'Submitted for review',
  resubmitted: 'Resubmitted after revision',
  admin_approved: 'Approved by admin',
  admin_returned: 'Returned to claimant by admin',
  auditor_approved: 'Approved by auditor',
  auditor_rejected: 'Rejected by auditor',
  auditor_returned: 'Returned by auditor for revision',
  auditor_note: 'Note added by auditor',
  claim_flagged: 'Flagged and sent back by reviewer',
  claim_flag_cleared: 'Flag cleared by reviewer',
  clarification_asked: 'Question asked by reviewer',
  clarification_closed: 'Question closed',
  regional_director_approved: 'Approved by regional director',
  regional_director_rejected: 'Sent back by regional director',
  regional_director_reviewed: 'Reviewed by regional director',
  global_director_approved: 'Final approval given',
  global_director_rejected: 'Sent back by global director',
  global_director_reviewed: 'Reviewed by global director',
  paid: 'Released for payment',
};

const humanize = (s) =>
  s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '-';

/** Label for an approval-chain stage. */
export const getStageLabel = (stage) => STAGE[stage] || humanize(stage);

/* ================================================================= ACCESSORS */

const decorate = (entry, status) => {
  const b = BUCKET[entry?.bucket] || BUCKET.gray;
  const [bg, text] = b.badge.split(' ');
  return {
    value: status,
    label: entry?.label || humanize(status),
    description: entry?.description || '',
    badge: b.badge,
    bg,
    text,
    border: b.border,
    solid: b.solid,
    terminal: !!entry?.terminal,
    known: !!entry,
  };
};

/**
 * Everything the UI needs for one claim status.
 * @param {string} status
 * @param {{ self?: boolean }} [opts] self=true when the reader is the claimant
 */
export const getClaimStatus = (status, { self = false } = {}) => {
  const entry = CLAIM[status];
  const base = decorate(entry, status);
  if (self && entry) {
    return {
      ...base,
      label: entry.selfLabel || entry.label,
      description: entry.selfDescription || entry.description,
    };
  }
  return base;
};

/** Roles that sign off at the final stage. */
const FINAL_APPROVERS = new Set(['global_director', 'finance_director']);

/**
 * Which role is being waited on, given a report's status and route.
 *
 * Returns null when the report is not waiting on anyone (draft, fully
 * approved, or sent back to the admin).
 */
const awaitingRole = (status, report) => {
  if (status === 'submitted') return 'auditor';
  if (status === 'regional_director_approved') return 'final';
  if (status === 'auditor_approved') {
    // A report whose regional stage was removed goes straight to the final
    // approver, so naming the regional director here would be wrong.
    return report?.approvalPath === 'skip_regional' ? 'final' : 'regional_director';
  }
  return null;
};

/**
 * Everything the UI needs for one report status.
 *
 * @param {string} status
 * @param {object} [report]  sharpens the wording: approvalPath decides whether
 *                           `auditor_approved` waits on the regional director or
 *                           the final approver, and regionalApproverName names a
 *                           stand-in instead of "the regional director".
 * @param {{viewerRole?: string, viewerId?: string}} [viewer]
 *        When the report is waiting on THIS reader, the label says so. Each
 *        director dashboard used to hand-roll that, with a different set of
 *        cases and a raw-token fallback for anything it had missed.
 */
export const getReportStatus = (status, report = null, viewer = {}) => {
  const base = decorate(REPORT[status], status);
  const waiting = awaitingRole(status, report);
  const { viewerRole, viewerId } = viewer;

  // Is the reader the one being waited on? A stand-in whose cover has ended
  // is not: the stage is back with the region's own director.
  const namedActive = !!report?.regionalApproverId && namedCoverActive(report);
  const isNamedStandIn = namedActive && !!viewerId
    && String(report.regionalApproverId) === String(viewerId);
  const waitingOnReader = viewerRole && (
    (waiting === 'auditor' && viewerRole === 'auditor')
    || (waiting === 'final' && FINAL_APPROVERS.has(viewerRole))
    || (waiting === 'regional_director'
        && (isNamedStandIn
            // No live stand-in: the region's own director is the approver.
            || (!namedActive && viewerRole === 'regional_director')))
  );

  if (waitingOnReader) {
    return {
      ...base,
      label: waiting === 'auditor' ? 'Awaiting Your Review' : 'Awaiting Your Approval',
      description: waiting === 'auditor'
        ? 'Submitted to you. Review the claims and approve or send it back.'
        : 'Signed at the previous stage and waiting on you.',
    };
  }

  if (status === 'auditor_approved' && report) {
    if (report.approvalPath === 'skip_regional') {
      return {
        ...base,
        label: 'Audited - Pending Final Approval',
        description: 'The regional stage was removed for this report. Waiting on the global or finance director.',
      };
    }
    if (report.regionalApproverName && namedActive) {
      return {
        ...base,
        label: `Audited - Pending ${report.regionalApproverName}`,
        description: `${report.regionalApproverName} was named to approve at the regional stage.`,
      };
    }
    if (report.regionalApproverName && report.regionalApproverId) {
      return {
        ...base,
        label: 'Audited - Pending Regional Director',
        description: `${report.regionalApproverName}'s cover ended on ${coverEndText(report.approverAccessEndsAt)}, so it is back with the regional director.`,
      };
    }
  }
  return base;
};

/** Everything the UI needs for one report LINE status. */
export const getReportClaimStatus = (status) => decorate(REPORT_CLAIM[status], status);

/* ============================================ CLAIMANT-FACING STATUS GROUPS */
/**
 * The claim states collapsed to what a CLAIMANT can act on.
 *
 * Their dashboard previously offered one filter per raw status, derived from
 * whatever was present, labelled with the admin-facing wording. That is both
 * more choices than the question warrants and the wrong question: the only one
 * a claimant is really asking is "is this waiting on me?".
 *
 * `statuses` is a set, not a single value, so several raw states fold into one
 * option. Keep 'draft' and 'paid' as their own groups - one is work the claimant
 * has not sent, the other is money that has arrived, and both are things people
 * look for directly.
 */
export const CLAIMANT_STATUS_GROUPS = [
  {
    value: 'draft',
    label: 'Drafts',
    description: 'Started but not submitted. Yours to finish.',
    statuses: ['draft'],
  },
  {
    value: 'in_progress',
    label: 'In Progress',
    description: 'Submitted and working through review. Nothing for you to do.',
    // 'approved' and 'under_review' sit here deliberately: to the claimant both
    // mean "being handled", and 'under_review' is a query between the auditor
    // and the admin that the claimant plays no part in.
    statuses: ['submitted', 'pending', 'approved', 'under_review'],
  },
  {
    value: 'action_needed',
    label: 'Needs Your Action',
    description: 'Sent back to you. Edit and resubmit.',
    statuses: ['returned'],
  },
  {
    value: 'paid',
    label: 'Paid',
    description: 'Approved for payment.',
    // A contractor's fully-approved claim belongs with the finished ones from
    // the claimant's point of view - it is done and nothing more is asked of
    // them, which is the only distinction this grouping draws.
    statuses: ['paid', 'finally_approved'],
  },
  {
    value: 'rejected',
    label: 'Rejected',
    description: 'Declined and closed.',
    statuses: ['rejected'],
  },
];

/** Does this claim belong in the given claimant group? */
export const claimInGroup = (status, groupValue) => {
  if (!groupValue) return true;
  const group = CLAIMANT_STATUS_GROUPS.find((g) => g.value === groupValue);
  return group ? group.statuses.includes(status) : false;
};

/**
 * The groups worth offering for a given set of claims, plus how many are in
 * each. An empty group is dropped rather than shown reading zero.
 */
export const claimantGroupOptions = (items = []) => {
  const counts = CLAIMANT_STATUS_GROUPS.map((g) => ({
    ...g,
    count: (items || []).filter((i) => g.statuses.includes(i?.status)).length,
  }));
  return counts.filter((g) => g.count > 0);
};

/** The label for a group value, for prose like "no X claims found". */
export const claimantGroupLabel = (groupValue) =>
  CLAIMANT_STATUS_GROUPS.find((g) => g.value === groupValue)?.label || '';

/* ================================================== BACK-COMPAT THIN WRAPPERS */
/* The five existing importers keep working unchanged. */

export const getStatusLabel = (status) => getClaimStatus(status).label;

export const getStatusColorClass = (status) => getClaimStatus(status).badge;

export const getReportStatusLabel = (status) => getReportStatus(status).label;

/**
 * Build claim filter options from the statuses present in a data set, in
 * workflow order, unknown values appended.
 *
 * The label-dedupe this used to apply is gone. It dropped any status whose label
 * collided with an earlier one - which silently removed the legacy 'pending'
 * option and made those rows unfilterable. Labels are now distinct by
 * construction, so there is nothing to dedupe.
 */
export const buildStatusFilterOptions = (items = []) => {
  const present = new Set((items || []).map((i) => i?.status).filter(Boolean));
  const known = CLAIM_ORDER.filter((s) => present.has(s));
  const extra = [...present].filter((s) => !CLAIM_ORDER.includes(s)).sort();
  return [...known, ...extra].map((value) => ({
    value,
    label: getStatusLabel(value),
    description: getClaimStatus(value).description,
  }));
};

/** Fixed report filter options - reports are filtered server-side. */
export const buildReportStatusFilterOptions = () =>
  REPORT_ORDER.map((value) => ({
    value,
    label: getReportStatusLabel(value),
    description: getReportStatus(value).description,
  }));

/**
 * Count a collection by status, for card strips.
 *
 * Takes the WHOLE population, never the filtered view: cards built from the
 * filtered list all read zero as soon as a filter is applied, because no row
 * can be in two statuses at once.
 */
export const countByStatus = (items = [], statuses = []) => {
  const counts = Object.create(null);
  for (const s of statuses) counts[s] = 0;
  for (const item of items || []) {
    const s = item?.status;
    if (s in counts) counts[s] += 1;
  }
  return counts;
};

const statusConfig = {
  getClaimStatus,
  getReportStatus,
  getReportClaimStatus,
  getStageLabel,
  getStatusLabel,
  getStatusColorClass,
  getReportStatusLabel,
  buildStatusFilterOptions,
  buildReportStatusFilterOptions,
  countByStatus,
  CLAIM_ORDER,
  CLAIM_CARD_SET,
  ADMIN_CLAIM_CARD_SET,
  CLAIMANT_STATUS_GROUPS,
  claimInGroup,
  claimantGroupOptions,
  claimantGroupLabel,
  REPORT_ORDER,
  REPORT_CARD_SET,
  REPORT_CLAIM_ORDER,
  REPORT_STATUSES,
};
export default statusConfig;
